import type { EntityType } from './extensions/EntityReference';
import { authHeaders, fetchWithAuthRetry, getApiUrl } from '../api/common';
import { searchEntities as searchProjectEntities, type SearchEntityType, type SearchProvider } from '../api/search';
import { fetchProjectRepositories, fetchProjectRepositoryBranches } from '../api/projects';
import { buildSkillLocalPath } from '../skillLocalPath';

import type { EntityResult } from '@agentteams/runner-request/ui/runtime';
export type { EntityResult } from '@agentteams/runner-request/ui/runtime';

type ConventionItem = { id: string; title: string; category: string; fileName: string };
type ReportItem = { id: string; title: string; status: string };
type PostMortemItem = { id: string; title: string; status: string };
type CoActionItem = { id: string; title: string; status: string };
type PlanItem = { id: string; title: string; status: string };
type DocumentItem = { id: string; title: string; tags: string[] };
type SkillItem = { id: string; title: string; slug: string; scope: string };

const commandToEntityType: Record<string, EntityType> = {
  convention: 'convention',
  document: 'document',
  skill: 'skill',
  report: 'completionReport',
  postmortem: 'postMortem',
  codereview: 'codeReview',
  coaction: 'coAction',
  plan: 'plan',
  github: 'GITHUB_ISSUE',
  gitlab: 'GITLAB_ISSUE',
  bitbucket: 'BITBUCKET_ISSUE',
  linear: 'LINEAR_ISSUE',
  sentry: 'SENTRY_ISSUE',
};

const commandToApiPath: Record<string, string> = {
  convention: 'conventions',
  document: 'documents',
  skill: 'skills',
  report: 'completion-reports',
  postmortem: 'post-mortems',
  codereview: 'code-reviews',
  coaction: 'co-actions',
  plan: 'plans',
};

export const commandToProvider: Partial<Record<string, SearchProvider>> = {
  github: 'github',
  gitlab: 'gitlab',
  bitbucket: 'bitbucket',
  linear: 'linear',
  sentry: 'sentry',
};

const commandToSearchTypes: Partial<Record<string, SearchEntityType[]>> = {
  github: ['GITHUB_ISSUE', 'GITHUB_PR'],
  gitlab: ['GITLAB_ISSUE', 'GITLAB_MERGE_REQUEST'],
  bitbucket: ['BITBUCKET_ISSUE', 'BITBUCKET_PR'],
  linear: ['LINEAR_ISSUE'],
  sentry: ['SENTRY_ISSUE'],
};

const commandToUnifiedSearchTypes: Partial<Record<string, SearchEntityType[]>> = {
  codereview: ['CODE_REVIEW'],
  document: ['DOCUMENT'],
};

const providerBadgeFallback: Record<SearchEntityType, string> = {
  PLAN: 'Plan',
  CO_ACTION: 'Co-Action',
  COMPLETION_REPORT: 'Report',
  POST_MORTEM: 'Post Mortem',
  CONVENTION: 'Convention',
  SKILL: 'Skill',
  COMMENT: 'Comment',
  CODE_REVIEW: 'Code Review',
  DOCUMENT: 'Document',
  GITHUB_ISSUE: 'GitHub Issue',
  GITHUB_PR: 'GitHub PR',
  GITLAB_ISSUE: 'GitLab Issue',
  GITLAB_MERGE_REQUEST: 'GitLab MR',
  BITBUCKET_ISSUE: 'Bitbucket Issue',
  BITBUCKET_PR: 'Bitbucket PR',
  LINEAR_ISSUE: 'Linear Issue',
  SENTRY_ISSUE: 'Sentry Issue',
};

const providerBadgeLabelKey: Record<SearchEntityType, string> = {
  PLAN: 'searchTypePlan',
  CO_ACTION: 'searchTypeCoAction',
  COMPLETION_REPORT: 'searchTypeCompletionReport',
  POST_MORTEM: 'searchTypePostMortem',
  CONVENTION: 'searchTypeConvention',
  SKILL: 'searchTypeSkill',
  COMMENT: 'searchTypeComment',
  CODE_REVIEW: 'searchTypeCodeReview',
  DOCUMENT: 'searchTypeDocument',
  GITHUB_ISSUE: 'searchTypeGithubIssue',
  GITHUB_PR: 'searchTypeGithubPr',
  GITLAB_ISSUE: 'searchTypeGitlabIssue',
  GITLAB_MERGE_REQUEST: 'searchTypeGitlabMergeRequest',
  BITBUCKET_ISSUE: 'searchTypeBitbucketIssue',
  BITBUCKET_PR: 'searchTypeBitbucketPr',
  LINEAR_ISSUE: 'searchTypeLinearIssue',
  SENTRY_ISSUE: 'searchTypeSentryIssue',
};

const resolveProviderBadgeLabel = (entityType: SearchEntityType, translate?: (key: string) => string): string => {
  if (!translate) return providerBadgeFallback[entityType];
  const labelKey = providerBadgeLabelKey[entityType];
  const translated = translate(labelKey);
  // next-intl returns the key itself when the key is missing; in that case fall
  // back to the English label so the badge is never literally "searchTypePlan".
  return translated && translated !== labelKey ? translated : providerBadgeFallback[entityType];
};

const searchEntityTypeToEntityType: Record<SearchEntityType, EntityType> = {
  PLAN: 'plan',
  CO_ACTION: 'coAction',
  COMPLETION_REPORT: 'completionReport',
  POST_MORTEM: 'postMortem',
  CONVENTION: 'convention',
  // D3 mention이 열려 skill은 슬래시/통합검색 멘션 후보다. SKILL 결과를 skill 칩으로 삽입한다.
  SKILL: 'skill',
  COMMENT: 'plan',
  CODE_REVIEW: 'codeReview',
  DOCUMENT: 'document',
  GITHUB_ISSUE: 'GITHUB_ISSUE',
  GITHUB_PR: 'GITHUB_PR',
  GITLAB_ISSUE: 'GITLAB_ISSUE',
  GITLAB_MERGE_REQUEST: 'GITLAB_MERGE_REQUEST',
  BITBUCKET_ISSUE: 'BITBUCKET_ISSUE',
  BITBUCKET_PR: 'BITBUCKET_PR',
  LINEAR_ISSUE: 'LINEAR_ISSUE',
  SENTRY_ISSUE: 'SENTRY_ISSUE',
};

const providerCacheByProject = new Map<string, SearchProvider[]>();

export const prefetchAvailableProviders = (token: string, projectId: string): void => {
  if (providerCacheByProject.has(projectId)) return;

  void searchProjectEntities(token, projectId, { q: '' })
    .then((data) => {
      providerCacheByProject.set(projectId, data.meta.availableProviders);
    })
    .catch(() => {});
};

export const getCachedAvailableProviders = (projectId: string): SearchProvider[] | null => {
  return providerCacheByProject.get(projectId) ?? null;
};

export const cacheAvailableProviders = (projectId: string, providers: SearchProvider[]): void => {
  providerCacheByProject.set(projectId, providers);
};

export const isExternalProviderCommand = (commandId: string): boolean => Boolean(commandToProvider[commandId]);

const BRANCH_RESULT_LIMIT = 20;
const branchProviders = new Set<string>(['GITHUB', 'GITLAB', 'BITBUCKET']);
const branchEntitiesByProject = new Map<string, EntityResult[]>();

// 프로젝트의 git 연동 레포 브랜치를 모아 엔티티 목록으로 캐시한다.
// 브랜치 조회 API는 검색어를 받지 않으므로 클라이언트에서 필터링한다.
const loadProjectBranchEntities = async (token: string, projectId: string): Promise<EntityResult[]> => {
  const cached = branchEntitiesByProject.get(projectId);
  if (cached) return cached;

  const repositories = await fetchProjectRepositories(token, projectId);
  const gitRepositories = repositories.filter((repo) => branchProviders.has(repo.provider));

  const branchLists = await Promise.all(
    gitRepositories.map(async (repo) => {
      try {
        const branches = await fetchProjectRepositoryBranches(token, projectId, repo.id);
        return branches.map<EntityResult>((branch) => ({
          entityType: 'branch',
          id: `${repo.id}:${branch.name}`,
          title: branch.name,
          badge: repo.name,
          insertLabel: `${repo.name}/${branch.name}`,
        }));
      } catch {
        return [];
      }
    }),
  );

  const entities = branchLists.flat();
  branchEntitiesByProject.set(projectId, entities);
  return entities;
};

// 외부에서 브랜치 변경 후 다음 조회 때 새로 받도록 캐시를 비운다.
export const clearProjectBranchEntitiesCache = (projectId?: string): void => {
  if (projectId) {
    branchEntitiesByProject.delete(projectId);
    return;
  }
  branchEntitiesByProject.clear();
};

export const searchCommandEntities = async (
  token: string,
  projectId: string,
  commandId: string,
  query: string,
  translate?: (key: string) => string,
  options?: { signal?: AbortSignal },
): Promise<EntityResult[]> => {
  const searchOptions = options?.signal ? { signal: options.signal } : undefined;

  if (commandId === 'branch') {
    try {
      const entities = await loadProjectBranchEntities(token, projectId);
      if (options?.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      const normalizedQuery = query.trim().toLowerCase();
      const filtered =
        normalizedQuery.length === 0
          ? entities
          : entities.filter((entity) => entity.title.toLowerCase().includes(normalizedQuery));
      return filtered.slice(0, BRANCH_RESULT_LIMIT);
    } catch {
      if (options?.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      return [];
    }
  }

  const externalSearchTypes = commandToSearchTypes[commandId];
  if (externalSearchTypes) {
    try {
      const params = {
        q: query,
        types: externalSearchTypes,
        limit: 10,
      };
      const response = searchOptions
        ? await searchProjectEntities(token, projectId, params, searchOptions)
        : await searchProjectEntities(token, projectId, params);

      return response.data.map((item) => ({
        entityType: searchEntityTypeToEntityType[item.entityType],
        id: item.id,
        title: item.title,
        badge: item.status ?? resolveProviderBadgeLabel(item.entityType, translate),
        htmlUrl: item.htmlUrl ?? undefined,
      }));
    } catch {
      if (options?.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      return [];
    }
  }

  const unifiedSearchTypes = commandToUnifiedSearchTypes[commandId];
  if (unifiedSearchTypes && query.trim().length > 0) {
    try {
      const params = {
        q: query,
        types: unifiedSearchTypes,
        limit: 10,
      };
      const response = searchOptions
        ? await searchProjectEntities(token, projectId, params, searchOptions)
        : await searchProjectEntities(token, projectId, params);

      return response.data.map((item) => ({
        entityType: searchEntityTypeToEntityType[item.entityType],
        id: item.id,
        title: item.title,
        badge: item.status ?? resolveProviderBadgeLabel(item.entityType, translate),
        htmlUrl: item.htmlUrl ?? undefined,
      }));
    } catch {
      if (options?.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      return [];
    }
  }

  const apiPath = commandToApiPath[commandId];
  if (!apiPath) return [];

  const params = new URLSearchParams();
  if (query.length > 0) params.set('search', query);
  params.set('pageSize', '10');

  try {
    const response = await fetchWithAuthRetry(
      `${getApiUrl()}/api/projects/${projectId}/${apiPath}?${params.toString()}`,
      { headers: authHeaders(token), signal: options?.signal },
    );

    if (!response.ok) return [];

    const json = (await response.json()) as {
      data: Array<ConventionItem | ReportItem | PostMortemItem | CoActionItem | PlanItem | DocumentItem | SkillItem>;
    };

    return json.data.map((item) => {
      const isSkill = commandId === 'skill';
      const badge =
        isSkill && 'slug' in item
          ? item.slug
          : 'category' in item
            ? item.category
            : 'status' in item
              ? item.status
              : resolveProviderBadgeLabel(commandToUnifiedSearchTypes[commandId]?.[0] ?? 'DOCUMENT', translate);
      const path =
        isSkill && 'slug' in item
          ? buildSkillLocalPath(item.slug)
          : 'fileName' in item && 'category' in item
            ? `.agentteams/${item.category}/${item.fileName}`
            : undefined;

      return {
        entityType: commandToEntityType[commandId],
        id: item.id,
        title: item.title,
        badge,
        path,
      };
    });
  } catch {
    if (options?.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    return [];
  }
};
